import React, { useState } from 'react';
import { PlusIcon, SearchIcon, EditIcon, TrashIcon, PhoneIcon, MailIcon, BuildingIcon, BriefcaseIcon, DownloadIcon, FileSpreadsheetIcon, ContactIcon, CalendarIcon, UserIcon } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Textarea } from '../ui/Textarea';
import { Modal } from '../ui/Modal';
import { useData } from '../../contexts/DataContext';
import { useAuth } from '../../contexts/AuthContext';
import { Contact } from '../../types';
import { exportContactsToExcel } from '../../utils/excelExport';
import { usePagination } from '../../hooks/usePagination';
import { Pagination } from '../ui/Pagination';
export function Contacts() {
  const {
    contacts,
    addContact,
    updateContact,
    deleteContact
  } = useData();
  const {
    user
  } = useAuth();
  const isAdmin = user?.role === 'administrador';
  const [searchTerm, setSearchTerm] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    company: '',
    position: '',
    notes: ''
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [isExporting, setIsExporting] = useState(false);
  const filteredContacts = contacts.filter(c => c.name.toLowerCase().includes(searchTerm.toLowerCase()) || c.company.toLowerCase().includes(searchTerm.toLowerCase()) || c.email.toLowerCase().includes(searchTerm.toLowerCase()));
  const {
    paginatedItems: paginatedContacts,
    currentPage,
    totalPages,
    totalItems,
    startIndex,
    endIndex,
    goToPage,
    nextPage,
    prevPage,
    resetPage
  } = usePagination(filteredContacts);
  const openNewForm = () => {
    setSelectedContact(null);
    setFormData({
      name: '',
      phone: '',
      email: '',
      company: '',
      position: '',
      notes: ''
    });
    setFormErrors({});
    setIsFormOpen(true);
  };
  const openEditForm = (contact: Contact) => {
    setSelectedContact(contact);
    setFormData({
      name: contact.name,
      phone: contact.phone,
      email: contact.email,
      company: contact.company,
      position: contact.position,
      notes: contact.notes
    });
    setFormErrors({});
    setIsFormOpen(true);
  };
  const openDeleteConfirm = (contact: Contact) => {
    setSelectedContact(contact);
    setIsDeleteConfirmOpen(true);
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const errors: Record<string, string> = {};
    if (!formData.name) errors.name = 'El nombre es requerido';
    if (!formData.email) errors.email = 'El correo es requerido';
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    if (selectedContact) {
      updateContact(selectedContact.id, formData);
    } else {
      addContact({
        ...formData,
        createdBy: user?.name || 'Sistema'
      });
    }
    setIsFormOpen(false);
  };
  const handleDelete = () => {
    if (selectedContact) {
      deleteContact(selectedContact.id);
      setIsDeleteConfirmOpen(false);
    }
  };
  const handleExportToExcel = async () => {
    setIsExporting(true);
    await new Promise(resolve => setTimeout(resolve, 500));
    exportContactsToExcel(contacts);
    setIsExporting(false);
  };
  return <div className="space-y-6" data-id="element-661">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-662">
        <div data-id="element-663">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-664">
            Contactos
          </h1>
          <p className="text-gray-500 dark:text-gray-400" data-id="element-665">
            Gestión de contactos B2B
          </p>
        </div>
        <Button leftIcon={<PlusIcon className="w-4 h-4" data-id="element-667" />} onClick={openNewForm} data-id="element-666">
          Nuevo Contacto
        </Button>
      </div>

      {/* Beautiful Export Card - Only for Admin */}
      {isAdmin && <Card className="bg-gradient-to-br from-yellow-50 to-amber-50 dark:from-yellow-950/30 dark:to-amber-950/30 border-yellow-200 dark:border-yellow-800" data-id="element-668">
          <div className="flex items-center justify-between" data-id="element-669">
            <div className="flex items-center gap-4" data-id="element-670">
              <div className="w-14 h-14 rounded-xl bg-yellow-500 dark:bg-yellow-600 flex items-center justify-center shadow-lg shadow-yellow-500/20" data-id="element-671">
                <FileSpreadsheetIcon className="w-7 h-7 text-white" data-id="element-672" />
              </div>
              <div data-id="element-673">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1" data-id="element-674">
                  Exportar Base de Datos
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400" data-id="element-675">
                  Descarga todos los contactos en formato Excel
                </p>
                <div className="flex items-center gap-4 mt-2" data-id="element-676">
                  <div className="flex items-center gap-1.5 text-sm text-yellow-700 dark:text-yellow-400" data-id="element-677">
                    <ContactIcon className="w-4 h-4" data-id="element-678" />
                    <span className="font-medium" data-id="element-679">
                      {contacts.length} contactos
                    </span>
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-400" data-id="element-680">
                    • Incluye: Nombre, Empresa, Cargo, Contacto, Notas
                  </div>
                </div>
              </div>
            </div>
            <Button leftIcon={<DownloadIcon className="w-4 h-4" data-id="element-682" />} onClick={handleExportToExcel} isLoading={isExporting} className="shadow-lg shadow-yellow-500/20 hover:shadow-xl hover:shadow-yellow-500/30 transition-all duration-200" data-id="element-681">
              {isExporting ? 'Exportando...' : 'Descargar Excel'}
            </Button>
          </div>
        </Card>}

      {/* Search */}
      <Card data-id="element-683">
        <div className="relative" data-id="element-684">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-685" />
          <Input placeholder="Buscar contactos..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-10" data-id="element-686" />
        </div>
      </Card>

      {/* Contacts Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-687">
        {paginatedContacts.length === 0 ? <div className="col-span-full text-center py-12 text-gray-500 dark:text-gray-400" data-id="element-688">
            No se encontraron contactos
          </div> : paginatedContacts.map(contact => <Card key={contact.id} data-id="element-689">
              <div className="flex items-start justify-between mb-4" data-id="element-690">
                <div className="flex items-center gap-3" data-id="element-691">
                  <div className="w-10 h-10 rounded-full bg-sky-100 dark:bg-sky-900 flex items-center justify-center text-sky-600 dark:text-sky-400 font-bold" data-id="element-692">
                    {contact.name.charAt(0)}
                  </div>
                  <div data-id="element-693">
                    <h3 className="font-semibold text-gray-900 dark:text-white" data-id="element-694">
                      {contact.name}
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-695">
                      {contact.position}
                    </p>
                  </div>
                </div>
                <div className="flex gap-1" data-id="element-696">
                  <button onClick={() => openEditForm(contact)} className="p-2 text-gray-500 hover:text-sky-600 hover:bg-sky-50 dark:hover:bg-sky-900/20 rounded-lg transition-colors" data-id="element-697">
                    <EditIcon className="w-4 h-4" data-id="element-698" />
                  </button>
                  <button onClick={() => openDeleteConfirm(contact)} className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors" data-id="element-699">
                    <TrashIcon className="w-4 h-4" data-id="element-700" />
                  </button>
                </div>
              </div>

              <div className="space-y-2 mb-4" data-id="element-701">
                <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400" data-id="element-702">
                  <BuildingIcon className="w-4 h-4" data-id="element-703" />
                  <span data-id="element-704">{contact.company}</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400" data-id="element-705">
                  <MailIcon className="w-4 h-4" data-id="element-706" />
                  <span data-id="element-707">{contact.email}</span>
                </div>
                {contact.phone && <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400" data-id="element-708">
                    <PhoneIcon className="w-4 h-4" data-id="element-709" />
                    <span data-id="element-710">{contact.phone}</span>
                  </div>}
              </div>

              {contact.notes && <p className="text-sm text-gray-500 dark:text-gray-400 italic border-t border-gray-100 dark:border-gray-700 pt-3 mb-3" data-id="element-711">
                  "{contact.notes}"
                </p>}

              {/* Created info */}
              <div className="border-t border-gray-100 dark:border-gray-700 pt-3 space-y-1.5" data-id="element-712">
                <div className="flex items-center gap-2 text-xs text-gray-400 dark:text-gray-500" data-id="element-713">
                  <CalendarIcon className="w-3.5 h-3.5" data-id="element-714" />
                  <span data-id="element-715">
                    Creado:{' '}
                    {new Date(contact.createdAt).toLocaleDateString('es-ES', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}
                  </span>
                </div>
                {contact.createdBy && <div className="flex items-center gap-2 text-xs text-gray-400 dark:text-gray-500" data-id="element-716">
                    <UserIcon className="w-3.5 h-3.5" data-id="element-717" />
                    <span data-id="element-718">Por: {contact.createdBy}</span>
                  </div>}
              </div>
            </Card>)}
      </div>

      {/* Pagination */}
      <Pagination currentPage={currentPage} totalPages={totalPages} totalItems={totalItems} startIndex={startIndex} endIndex={endIndex} onPageChange={goToPage} onNext={nextPage} onPrev={prevPage} data-id="element-719" />

      {/* Form Modal */}
      <Modal isOpen={isFormOpen} onClose={() => setIsFormOpen(false)} title={selectedContact ? 'Editar Contacto' : 'Nuevo Contacto'} size="md" icon={<ContactIcon className="w-5 h-5" data-id="element-721" />} accentColor="amber" data-id="element-720">
        <form onSubmit={handleSubmit} className="space-y-6" data-id="element-722">
          {/* Personal Info */}
          <div data-id="element-723">
            <div className="flex items-center gap-2 mb-4" data-id="element-724">
              <span className="w-1.5 h-5 rounded-full bg-amber-500" data-id="element-725"></span>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide" data-id="element-726">
                Información Personal
              </h3>
            </div>
            <div className="bg-gray-50 dark:bg-gray-900/30 rounded-xl p-4 space-y-4 border border-gray-100 dark:border-gray-700/50" data-id="element-727">
              <Input label="Nombre Completo" value={formData.name} onChange={e => setFormData({
              ...formData,
              name: e.target.value
            })} error={formErrors.name} placeholder="Nombre del contacto" data-id="element-728" />
              <div className="grid grid-cols-2 gap-4" data-id="element-729">
                <Input label="Empresa" value={formData.company} onChange={e => setFormData({
                ...formData,
                company: e.target.value
              })} placeholder="Nombre de la empresa" data-id="element-730" />
                <Input label="Cargo / Posición" value={formData.position} onChange={e => setFormData({
                ...formData,
                position: e.target.value
              })} placeholder="Cargo del contacto" data-id="element-731" />
              </div>
            </div>
          </div>

          {/* Contact Info */}
          <div data-id="element-732">
            <div className="flex items-center gap-2 mb-4" data-id="element-733">
              <span className="w-1.5 h-5 rounded-full bg-orange-500" data-id="element-734"></span>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide" data-id="element-735">
                Contacto
              </h3>
            </div>
            <div className="bg-gray-50 dark:bg-gray-900/30 rounded-xl p-4 space-y-4 border border-gray-100 dark:border-gray-700/50" data-id="element-736">
              <div className="grid grid-cols-2 gap-4" data-id="element-737">
                <Input label="Teléfono" value={formData.phone} onChange={e => setFormData({
                ...formData,
                phone: e.target.value
              })} placeholder="809-000-0000" data-id="element-738" />
                <Input type="email" label="Correo Electrónico" value={formData.email} onChange={e => setFormData({
                ...formData,
                email: e.target.value
              })} error={formErrors.email} placeholder="contacto@email.com" data-id="element-739" />
              </div>
              <Textarea label="Notas" value={formData.notes} onChange={e => setFormData({
              ...formData,
              notes: e.target.value
            })} rows={3} placeholder="Notas adicionales sobre el contacto..." data-id="element-740" />
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-100 dark:border-gray-700" data-id="element-741">
            <Button variant="outline" type="button" onClick={() => setIsFormOpen(false)} data-id="element-742">
              Cancelar
            </Button>
            <Button type="submit" data-id="element-743">
              {selectedContact ? 'Guardar Cambios' : 'Crear Contacto'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation */}
      <Modal isOpen={isDeleteConfirmOpen} onClose={() => setIsDeleteConfirmOpen(false)} title="Confirmar Eliminación" size="sm" data-id="element-744">
        <div className="space-y-4" data-id="element-745">
          <p className="text-gray-600 dark:text-gray-400" data-id="element-746">
            ¿Está seguro de que desea eliminar a "{selectedContact?.name}"?
          </p>
          <div className="flex justify-end gap-3" data-id="element-747">
            <Button variant="outline" onClick={() => setIsDeleteConfirmOpen(false)} data-id="element-748">
              Cancelar
            </Button>
            <Button variant="danger" onClick={handleDelete} data-id="element-749">
              Eliminar
            </Button>
          </div>
        </div>
      </Modal>
    </div>;
}